import { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { X, Undo2, Redo2, Trash2, Check, Pen, Highlighter, Pencil, Eraser, Type, Quote } from 'lucide-react';
import * as fabric from 'fabric';
import { EraserBrush } from '@erase2d/fabric';

const TOOLS = {
  pen:       { label: 'Pen',     icon: Pen,         defaultWidth: 4,  defaultOpacity: 1.0, decimate: 2 },
  marker:    { label: 'Marker',  icon: Highlighter,  defaultWidth: 20, defaultOpacity: 0.4, decimate: 4 },
  pencil:    { label: 'Pencil',  icon: Pencil,       defaultWidth: 2,  defaultOpacity: 0.6, decimate: 1 },
  eraser:    { label: 'Eraser',  icon: Eraser,       defaultWidth: 16, defaultOpacity: 1.0, decimate: 2 },
  text:      { label: 'Add Text', icon: Type,         defaultWidth: 24, defaultOpacity: 1.0, decimate: 0 },
};

const FONTS = [
  { id: 'sans', label: 'Sans', family: 'system-ui, sans-serif' },
  { id: 'great-vibes', label: 'Script', family: "'Great Vibes', cursive" },
  { id: 'dancing', label: 'Casual', family: "'Dancing Script', cursive" },
  { id: 'pacifico', label: 'Round', family: "'Pacifico', cursive" },
  { id: 'montserrat', label: 'Clean', family: "'Montserrat', sans-serif" },
  { id: 'georgia', label: 'Serif', family: "Georgia, serif" },
];

const QUOTES = [
  "Every artist was first an amateur.",
  "Creativity takes courage.",
  "Art is not what you see, but what you make others see.",
  "The earth has music for those who listen.",
  "In the middle of difficulty lies opportunity.",
  "Be yourself; everyone else is already taken.",
  "Stars can't shine without darkness.",
  "Create the things you wish existed.",
  "Life is a canvas; make it a masterpiece.",
  "The best way to predict the future is to create it.",
  "Stay wild, flower child.",
  "Dream big, sparkle more, shine bright.",
];

const COLORS = [
  '#000000', '#ffffff', '#4b5563', '#9ca3af',
  '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6',
  '#ec4899', '#a855f7',
];

export default function DrawingCanvas({ imageUrl, onSave, onClose }) {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const containerRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const pressureRef = useRef(0.5);
  const activeToolRef = useRef('pen');
  const toolSettingsRef = useRef(null);

  const [activeTool, setActiveTool] = useState('pen');
  const [toolSettings, setToolSettings] = useState({
    pen:    { color: '#000000', width: 4,  opacity: 1.0 },
    marker: { color: '#f59e0b', width: 20, opacity: 0.4 },
    pencil: { color: '#4b5563', width: 2,  opacity: 0.6 },
    eraser: { color: '#ffffff', width: 16, opacity: 1.0 },
    text:   { color: '#000000', width: 24, opacity: 1.0 },
  });
  const [isReady, setIsReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [textFont, setTextFont] = useState(FONTS[0]);
  const [showQuotes, setShowQuotes] = useState(false);

  const currentSettings = toolSettings[activeTool];
  activeToolRef.current = activeTool;
  toolSettingsRef.current = toolSettings;

  const updateToolSetting = useCallback((key, value) => {
    setToolSettings(prev => ({
      ...prev,
      [activeTool]: { ...prev[activeTool], [key]: value },
    }));
  }, [activeTool]);

  const applyBrush = useCallback((canvas, tool, settings) => {
    if (!canvas) return;
    const toolDef = TOOLS[tool];

    if (tool === 'eraser') {
      const eraser = new EraserBrush(canvas);
      eraser.width = settings.width;
      canvas.freeDrawingBrush = eraser;
      return;
    }

    const brush = new fabric.PencilBrush(canvas);
    brush.width = settings.width;
    brush.decimate = toolDef.decimate;

    const hexOpacity = Math.round(settings.opacity * 255).toString(16).padStart(2, '0');
    brush.color = settings.color + hexOpacity;

    canvas.freeDrawingBrush = brush;
  }, []);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const canvas = new fabric.Canvas(canvasRef.current, {
      width,
      height,
      isDrawingMode: true,
      backgroundColor: '#ffffff',
    });

    fabricRef.current = canvas;
    undoStackRef.current = [];
    redoStackRef.current = [];

    applyBrush(canvas, 'pen', toolSettings.pen);

    // Pressure sensitivity via pointer events
    const upperCanvas = canvas.upperCanvasEl;
    const handlePointerMove = (e) => {
      if (e.pressure > 0 && e.pressure < 1) {
        pressureRef.current = e.pressure;
        const brush = canvas.freeDrawingBrush;
        if (brush) {
          const tool = activeToolRef.current;
          const toolDef = TOOLS[tool] || TOOLS.pen;
          const settings = toolSettingsRef.current?.[tool];
          const baseWidth = settings?.width ?? toolDef.defaultWidth;
          brush.width = baseWidth * (0.3 + e.pressure * 1.2);
        }
      }
    };
    upperCanvas.addEventListener('pointermove', handlePointerMove);

    canvas.on('path:created', ({ path }) => {
      if (path) path.erasable = true;
      redoStackRef.current = [];
      setCanRedo(false);
      setCanUndo(true);
    });

    if (imageUrl) {
      const loadImageOntoCanvas = (imgSrc) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const fabricImg = new fabric.FabricImage(img);
          const scaleX = width / img.width;
          const scaleY = height / img.height;
          const scale = Math.min(scaleX, scaleY);

          fabricImg.set({
            scaleX: scale,
            scaleY: scale,
            left: (width - img.width * scale) / 2,
            top: (height - img.height * scale) / 2,
            selectable: false,
            evented: false,
            erasable: false,
          });

          canvas.insertAt(0, fabricImg);
          canvas.renderAll();
          setIsReady(true);
        };
        img.onerror = () => {
          console.error('Failed to load image for drawing canvas');
          setIsReady(true);
        };
        img.src = imgSrc;
      };

      // Try fetch-as-blob first (avoids CORS taint); fall back to direct load
      fetch(imageUrl, { mode: 'cors' })
        .then(res => res.blob())
        .then(blob => loadImageOntoCanvas(URL.createObjectURL(blob)))
        .catch(() => {
          // CORS fetch failed; load directly (canvas may be tainted but image still displays)
          console.warn('CORS fetch failed, loading image directly');
          loadImageOntoCanvas(imageUrl);
        });
    } else {
      setIsReady(true);
    }

    return () => {
      upperCanvas.removeEventListener('pointermove', handlePointerMove);
      canvas.dispose();
      fabricRef.current = null;
    };
  }, [imageUrl]);

  // Update brush when tool or settings change
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    if (activeTool === 'text') {
      canvas.isDrawingMode = false;
      canvas.defaultCursor = 'default';
      canvas.selection = true;
    } else {
      canvas.isDrawingMode = true;
      canvas.defaultCursor = 'default';
      canvas.selection = false;
      canvas.discardActiveObject();
      canvas.renderAll();
      applyBrush(canvas, activeTool, currentSettings);
    }
  }, [activeTool, currentSettings, applyBrush]);

  const addTextObject = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const settings = toolSettingsRef.current?.text;
    const textObj = new fabric.IText('Tap to edit', {
      left: canvas.width / 2,
      top: canvas.height / 2,
      fontFamily: textFont.family,
      fontSize: settings?.width || 24,
      fill: settings?.color || '#000000',
      editable: true,
      selectable: true,
      erasable: true,
      originX: 'center',
      originY: 'center',
    });
    canvas.add(textObj);
    canvas.setActiveObject(textObj);
    textObj.enterEditing();
    textObj.selectAll();
    canvas.renderAll();

    redoStackRef.current = [];
    setCanRedo(false);
    setCanUndo(true);
  }, [textFont]);

  const getDrawObjects = useCallback((canvas) => {
    return canvas.getObjects().filter(o => !(o instanceof fabric.FabricImage && !o.selectable));
  }, []);

  const handleUndo = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const drawObjects = getDrawObjects(canvas);
    if (drawObjects.length === 0) return;

    const last = drawObjects[drawObjects.length - 1];
    canvas.remove(last);
    redoStackRef.current.push(last);
    canvas.renderAll();

    setCanUndo(getDrawObjects(canvas).length > 0);
    setCanRedo(true);
  }, [getDrawObjects]);

  const handleRedo = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas || redoStackRef.current.length === 0) return;

    const obj = redoStackRef.current.pop();
    canvas.add(obj);
    canvas.renderAll();

    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const handleClear = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const drawObjects = getDrawObjects(canvas);
    drawObjects.forEach(o => {
      redoStackRef.current.push(o);
      canvas.remove(o);
    });
    canvas.renderAll();
    setCanUndo(false);
    setCanRedo(true);
  }, [getDrawObjects]);

  const handleDone = useCallback(async () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    setIsSaving(true);
    try {
      const dataUrl = canvas.toDataURL({ format: 'jpeg', quality: 0.85 });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      await onSave(blob);
    } catch (err) {
      console.error('Failed to save drawing:', err);
    } finally {
      setIsSaving(false);
    }
  }, [onSave]);

  const showOpacitySlider = activeTool === 'marker' || activeTool === 'pencil';
  const isTextTool = activeTool === 'text';

  const handleFontChange = useCallback((font) => {
    setTextFont(font);
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (active && active instanceof fabric.IText) {
      active.set('fontFamily', font.family);
      canvas.renderAll();
    }
  }, []);

  const handleQuoteSelect = useCallback((quote) => {
    setShowQuotes(false);
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (active && active instanceof fabric.IText) {
      active.set('text', quote);
      canvas.renderAll();
      return;
    }
    const settings = toolSettingsRef.current?.text;
    const textObj = new fabric.IText(quote, {
      left: canvas.width / 2,
      top: canvas.height / 2,
      fontFamily: textFont.family,
      fontSize: settings?.width || 24,
      fill: settings?.color || '#000000',
      editable: true,
      selectable: true,
      erasable: true,
      originX: 'center',
      originY: 'center',
    });
    canvas.add(textObj);
    canvas.setActiveObject(textObj);
    redoStackRef.current = [];
    setCanRedo(false);
    setCanUndo(true);
  }, [textFont]);

  // Update active text object color/size when settings change
  useEffect(() => {
    if (!isTextTool) return;
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (active && active instanceof fabric.IText) {
      active.set('fill', currentSettings.color);
      active.set('fontSize', currentSettings.width);
      canvas.renderAll();
    }
  }, [isTextTool, currentSettings.color, currentSettings.width]);

  return (
    <Overlay>
      <Toolbar>
        <ToolBtn onClick={onClose}><X size={22} /></ToolBtn>
        <ToolGroup>
          <ToolBtn onClick={handleUndo} disabled={!canUndo}><Undo2 size={20} /></ToolBtn>
          <ToolBtn onClick={handleRedo} disabled={!canRedo}><Redo2 size={20} /></ToolBtn>
          <ToolBtn onClick={handleClear}><Trash2 size={20} /></ToolBtn>
        </ToolGroup>
        <DoneBtn onClick={handleDone} disabled={isSaving}>
          <Check size={20} />
          {isSaving ? 'Saving...' : 'Done'}
        </DoneBtn>
      </Toolbar>

      <CanvasContainer ref={containerRef}>
        <canvas ref={canvasRef} />
        {!isReady && <LoadingOverlay>Loading image...</LoadingOverlay>}
      </CanvasContainer>

      <BottomBar>
        <ToolRow>
          {Object.entries(TOOLS).map(([key, tool]) => {
            const Icon = tool.icon;
            return (
              <ToolTypeBtn
                key={key}
                $active={activeTool === key}
                onClick={() => {
                  if (key === 'text') {
                    setActiveTool('text');
                    addTextObject();
                  } else {
                    setActiveTool(key);
                  }
                }}
                title={tool.label}
              >
                <Icon size={20} />
                <ToolLabel>{tool.label}</ToolLabel>
              </ToolTypeBtn>
            );
          })}
        </ToolRow>

        {isTextTool && (
          <FontRow>
            {FONTS.map(f => (
              <FontBtn
                key={f.id}
                $active={textFont.id === f.id}
                onClick={() => handleFontChange(f)}
              >
                <FontPreview style={{ fontFamily: f.family }}>{f.label}</FontPreview>
              </FontBtn>
            ))}
            <QuoteBtn onClick={() => setShowQuotes(!showQuotes)} $active={showQuotes}>
              <Quote size={16} />
            </QuoteBtn>
          </FontRow>
        )}

        {isTextTool && showQuotes && (
          <QuotesPanel>
            {QUOTES.map((q, i) => (
              <QuoteItem key={i} onClick={() => handleQuoteSelect(q)}>
                {q}
              </QuoteItem>
            ))}
          </QuotesPanel>
        )}

        {activeTool !== 'eraser' && (
          <ColorRow>
            {COLORS.map(c => (
              <ColorSwatch
                key={c}
                $color={c}
                $active={currentSettings.color === c}
                onClick={() => updateToolSetting('color', c)}
              />
            ))}
            <CustomColorWrapper>
              <CustomColorInput
                type="color"
                value={currentSettings.color}
                onChange={e => updateToolSetting('color', e.target.value)}
              />
              <CustomColorLabel>+</CustomColorLabel>
            </CustomColorWrapper>
          </ColorRow>
        )}

        <SliderRow>
          <SliderGroup>
            <SliderLabel>{isTextTool ? 'Font Size' : 'Size'}</SliderLabel>
            <RangeInput
              type="range"
              min={isTextTool ? 12 : 1}
              max={isTextTool ? 72 : (activeTool === 'eraser' ? 60 : 40)}
              value={currentSettings.width}
              onChange={e => updateToolSetting('width', Number(e.target.value))}
            />
            <SliderValue>{currentSettings.width}{isTextTool ? 'pt' : 'px'}</SliderValue>
          </SliderGroup>
          {showOpacitySlider && (
            <SliderGroup>
              <SliderLabel>Opacity</SliderLabel>
              <RangeInput
                type="range"
                min={5}
                max={100}
                value={Math.round(currentSettings.opacity * 100)}
                onChange={e => updateToolSetting('opacity', Number(e.target.value) / 100)}
              />
              <SliderValue>{Math.round(currentSettings.opacity * 100)}%</SliderValue>
            </SliderGroup>
          )}
        </SliderRow>
      </BottomBar>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.background};
  z-index: 300;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  padding-top: max(8px, env(safe-area-inset-top));
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const ToolGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const ToolBtn = styled.button`
  padding: 8px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const DoneBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
`;

const CanvasContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  touch-action: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BottomBar = styled.div`
  padding: 8px 12px;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const ToolRow = styled.div`
  display: flex;
  gap: 2px;
  justify-content: center;
`;

const ToolTypeBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ $active, theme }) => ($active ? theme.colors.accent : 'transparent')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.textSecondary)};
  transition: all 0.15s;

  &:hover {
    background: ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.surface)};
  }
`;

const ToolLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const FontRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const FontBtn = styled.button`
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.textSecondary)};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.border)};
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FontPreview = styled.span`
  font-size: 13px;
  font-weight: 500;
`;

const QuoteBtn = styled.button`
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.textSecondary)};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.accent : theme.colors.border)};
  transition: all 0.15s;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const QuotesPanel = styled.div`
  max-height: 120px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.medium};
  padding: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const QuoteItem = styled.button`
  text-align: left;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.4;
  transition: background 0.1s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const ColorRow = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;

const ColorSwatch = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2px solid ${({ $active, $color, theme }) =>
    $active ? theme.colors.accent : ($color === '#ffffff' ? theme.colors.border : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 0 0 2px rgba(245, 158, 11, 0.3)' : 'none')};
  transition: all 0.15s;
  flex-shrink: 0;
`;

const CustomColorWrapper = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const CustomColorInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const CustomColorLabel = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textTertiary};
  pointer-events: none;
`;

const SliderRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const SliderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const SliderLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textTertiary};
  min-width: 40px;
`;

const RangeInput = styled.input`
  flex: 1;
  height: 4px;
  appearance: none;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 2px;
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent};
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent};
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
`;

const SliderValue = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 32px;
  text-align: right;
`;
